import { useMemo, useState } from 'react';
import styles from './Calculator.module.css';

type GeneticUnit = 'cM' | 'cR' | 'M';
type PhysicalUnit = 'bp' | 'kb' | 'Mb';

const speciesOptions = [
  { label: 'Generico (1 cM ≈ 1 Mb)', ratio: 1.0 },
  { label: 'Maiz (1 cM ≈ 0.7 Mb)', ratio: 0.7 },
  { label: 'Trigo (1 cM ≈ 0.25 Mb)', ratio: 0.25 },
  { label: 'Soja (1 cM ≈ 1.5 Mb)', ratio: 1.5 },
  { label: 'Arroz (1 cM ≈ 0.28 Mb)', ratio: 0.28 },
  { label: 'Tomate (1 cM ≈ 0.35 Mb)', ratio: 0.35 },
  { label: 'Arabidopsis (1 cM ≈ 4.6 Mb)', ratio: 4.6 },
];

const calculateRecombinationFrequency = (cM: number): number => {
  const d = cM / 100;
  if (d < 0.01) return d;
  const exp4d = Math.exp(4 * d);
  const r = (exp4d - 1) / (2 * (exp4d + 1));
  return Math.min(0.5, r);
};

const formatLocale = (value: number): string => value.toLocaleString();

export const UnitConverter: React.FC = () => {
  const [ratio, setRatio] = useState<number>(1.0);
  const [geneticValue, setGeneticValue] = useState<string>('');
  const [geneticUnit, setGeneticUnit] = useState<GeneticUnit>('cM');
  const [physicalValue, setPhysicalValue] = useState<string>('');
  const [physicalUnit, setPhysicalUnit] = useState<PhysicalUnit>('bp');

  const [geneticResult, setGeneticResult] = useState<string>('-');
  const [physicalResult, setPhysicalResult] = useState<string>('-');
  const [recombinationInfo, setRecombinationInfo] = useState<string>(
    'Ingrese una distancia genetica para calcular la frecuencia de recombinacion entre marcadores.'
  );

  const convertGeneticToPhysical = (rawValue: string, unit: GeneticUnit, activeRatio: number): void => {
    const value = Number(rawValue);
    if (Number.isNaN(value) || value < 0) {
      setPhysicalResult('-');
      setRecombinationInfo('Ingrese una distancia genetica valida.');
      return;
    }

    let cM = value;
    if (unit === 'M') cM = value * 100;
    if (unit === 'cR') cM = value;

    const Mb = cM * activeRatio;
    const bp = Mb * 1_000_000;
    const kb = Mb * 1_000;

    setPhysicalResult(`${formatLocale(bp)} bp | ${formatLocale(kb)} kb | ${Mb.toFixed(3)} Mb`);
    setGeneticResult(`${cM.toFixed(3)} cM | ${(cM / 100).toFixed(5)} M`);

    const r = calculateRecombinationFrequency(cM);
    setRecombinationInfo(
      `Distancia ${cM.toFixed(2)} cM: recombinacion ${(r * 100).toFixed(2)}%, ligamiento ${(
        (1 - r) *
        100
      ).toFixed(2)}% ${r < 0.5 ? '(marcadores ligados)' : '(no ligados)'}.`
    );
  };

  const convertPhysicalToGenetic = (rawValue: string, unit: PhysicalUnit, activeRatio: number): void => {
    const value = Number(rawValue);
    if (Number.isNaN(value) || value < 0) {
      setGeneticResult('-');
      return;
    }

    let Mb = value;
    if (unit === 'bp') Mb = value / 1_000_000;
    if (unit === 'kb') Mb = value / 1_000;

    const cM = Mb / activeRatio;
    const M = cM / 100;
    const cR = cM;

    setGeneticResult(`${cM.toFixed(3)} cM | ${M.toFixed(5)} M | ~${cR.toFixed(3)} cR`);
    const bp = Mb * 1_000_000;
    const kb = Mb * 1_000;
    setPhysicalResult(`${formatLocale(bp)} bp | ${formatLocale(kb)} kb | ${Mb.toFixed(3)} Mb`);

    const r = calculateRecombinationFrequency(cM);
    setRecombinationInfo(
      `Distancia ${cM.toFixed(2)} cM (${Mb.toFixed(2)} Mb): recombinacion ${(r * 100).toFixed(
        2
      )}%, ligamiento ${((1 - r) * 100).toFixed(2)}% ${r < 0.5 ? '(marcadores ligados)' : '(no ligados)'}.`
    );
  };

  const handleRatioChange = (newRatio: number): void => {
    setRatio(newRatio);
    if (geneticValue) {
      convertGeneticToPhysical(geneticValue, geneticUnit, newRatio);
    }
    if (physicalValue) {
      convertPhysicalToGenetic(physicalValue, physicalUnit, newRatio);
    }
  };

  const speciesTable = useMemo(
    () => [
      ['Maiz (Zea mays)', '2,300', '~3,000', '0.7'],
      ['Trigo (Triticum aestivum)', '17,000', '~4,300', '0.25'],
      ['Soja (Glycine max)', '1,115', '~1,800', '1.5'],
      ['Arroz (Oryza sativa)', '430', '~1,500', '0.28'],
      ['Tomate (Solanum lycopersicum)', '900', '~1,300', '0.35'],
      ['Arabidopsis thaliana', '135', '~620', '4.6'],
    ],
    []
  );

  return (
    <div className={styles.calculatorContainer}>
      <div className={styles.converterBox}>
        <div className={styles.calculatorHeader}>
          <h2>Convertidor de Unidades Geneticas</h2>
          <p>Convierta entre distancia genetica y fisica para analisis de mapeo y ligamiento.</p>
        </div>

        <div className={styles.speciesSelector}>
          <label htmlFor="species">Seleccionar especie (relacion cM/Mb):</label>
          <select
            id="species"
            className={styles.selectField}
            value={ratio}
            onChange={(event) => handleRatioChange(Number(event.target.value))}
          >
            {speciesOptions.map((option) => (
              <option key={option.label} value={option.ratio}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.converterGrid}>
          <div className={styles.converterPanel}>
            <h3>Distancia Genetica</h3>
            <div className={styles.inputGroup}>
              <label htmlFor="geneticValue">Valor</label>
              <input
                id="geneticValue"
                type="number"
                step="0.001"
                value={geneticValue}
                onChange={(event) => {
                  const value = event.target.value;
                  setGeneticValue(value);
                  setPhysicalValue('');
                  convertGeneticToPhysical(value, geneticUnit, ratio);
                }}
                placeholder="Ej: 25.5"
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="geneticUnit">Unidad</label>
              <select
                id="geneticUnit"
                className={styles.selectField}
                value={geneticUnit}
                onChange={(event) => {
                  const unit = event.target.value as GeneticUnit;
                  setGeneticUnit(unit);
                  if (geneticValue) convertGeneticToPhysical(geneticValue, unit, ratio);
                }}
              >
                <option value="cM">centiMorgan (cM)</option>
                <option value="cR">centiRay (cR)</option>
                <option value="M">Morgan (M)</option>
              </select>
            </div>
            <div className={styles.resultValueBox}>{geneticResult}</div>
          </div>

          <div className={styles.arrowContainer}>⇄</div>

          <div className={styles.converterPanel}>
            <h3>Distancia Fisica</h3>
            <div className={styles.inputGroup}>
              <label htmlFor="physicalValue">Valor</label>
              <input
                id="physicalValue"
                type="number"
                step="0.001"
                value={physicalValue}
                onChange={(event) => {
                  const value = event.target.value;
                  setPhysicalValue(value);
                  setGeneticValue('');
                  convertPhysicalToGenetic(value, physicalUnit, ratio);
                }}
                placeholder="Ej: 2500000"
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="physicalUnit">Unidad</label>
              <select
                id="physicalUnit"
                className={styles.selectField}
                value={physicalUnit}
                onChange={(event) => {
                  const unit = event.target.value as PhysicalUnit;
                  setPhysicalUnit(unit);
                  if (physicalValue) convertPhysicalToGenetic(physicalValue, unit, ratio);
                }}
              >
                <option value="bp">pares de bases (bp)</option>
                <option value="kb">kilobases (kb)</option>
                <option value="Mb">Megabases (Mb)</option>
              </select>
            </div>
            <div className={styles.resultValueBox}>{physicalResult}</div>
          </div>
        </div>

        <div className={styles.infoBox}>
          <h4>Frecuencia de Recombinacion</h4>
          <p>{recombinationInfo}</p>
        </div>

        <div className={styles.infoBox}>
          <h4>Relacion cM/Mb por especie</h4>
          <div className={styles.tableWrapper}>
            <table className={styles.conversionTable}>
              <thead>
                <tr>
                  <th>Especie</th>
                  <th>Genoma (Mb)</th>
                  <th>Mapa Genetico (cM)</th>
                  <th>Relacion (Mb/cM)</th>
                </tr>
              </thead>
              <tbody>
                {speciesTable.map((row) => (
                  <tr key={row[0]}>
                    <td>{row[0]}</td>
                    <td>{row[1]}</td>
                    <td>{row[2]}</td>
                    <td>{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={styles.smallNote}>
            Nota: la relacion cM/Mb cambia segun la region genomica por hotspots y zonas de baja
            recombinacion.
          </p>
        </div>

        <div className={styles.exampleSection}>
          <h4>Ejemplos de uso</h4>
          <p>
            Mapeo de QTL en maiz: un QTL a 45 cM corresponde a ~31.5 Mb usando 0.7 Mb/cM.
          </p>
          <p>
            MAS: dos marcadores separados por 15 cM tienen recombinacion ~13.9%, indicando
            ligamiento fuerte.
          </p>
          <p>
            Diseno de SNPs en soja: para cobertura cada 5 cM se requieren SNPs cada ~7.5 Mb.
          </p>
        </div>
      </div>
    </div>
  );
};
